import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'wouter';
import { AnimatePresence, motion, useDragControls, type PanInfo } from 'motion/react';
import { InstallAppControl } from '../components/InstallApp';
import { useTags } from '../tags/store';
import { AccountAccessRow } from './AccountAccess';
import {
  NAV_GLYPHS,
  NAV_SECTIONS,
  NOTES_SECTION,
  OPERATOR_SECTION,
  isAdvisorsActive,
  isIdeaCaptureActive,
  sectionContainsActive,
  sectionMeta,
  teamSection,
  type NavCtx,
  type NavItem,
  type NavSectionDef,
} from './navConfig';

// ---------- Bottom tab bar ----------

const ic = {
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="6" height="6" rx="1.2" />
      <rect x="14" y="4" width="6" height="6" rx="1.2" />
      <rect x="4" y="14" width="6" height="6" rx="1.2" />
      <rect x="14" y="14" width="6" height="6" rx="1.2" />
    </svg>
  ),
  spark: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <path d="M12 3c.5 4.5 2.5 6.5 7 7-4.5.5-6.5 2.5-7 7-.5-4.5-2.5-6.5-7-7 4.5-.5 6.5-2.5 7-7Z" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3 3 0 0 1 0 5.6M17.5 14.2A5.5 5.5 0 0 1 20.5 19" />
    </svg>
  ),
  grid: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6" cy="6" r="1.6" />
      <circle cx="12" cy="6" r="1.6" />
      <circle cx="18" cy="6" r="1.6" />
      <circle cx="6" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="18" cy="12" r="1.6" />
      <circle cx="6" cy="18" r="1.6" />
      <circle cx="12" cy="18" r="1.6" />
      <circle cx="18" cy="18" r="1.6" />
    </svg>
  ),
  close: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  ),
};

/**
 * Phone navigation (<768px): an always-visible bottom tab bar replaces the
 * hidden hamburger drawer. Its last tab opens "Everything in EntreDash", a
 * sheet showing every section at once.
 *
 * `hideBar` is for full-height chat screens whose composer owns the bottom
 * edge: the bar is hidden there and the sheet opens from the top-bar button.
 */
export function MobileNav({
  ctx,
  isAdmin,
  roles = [],
  hideBar,
  menuOpen,
  setMenuOpen,
  onCapture,
}: {
  /** The Capture tab opens the quick "What's the idea?" dialog instead of navigating. */
  onCapture?: () => void;
  ctx: NavCtx;
  isAdmin: boolean;
  roles?: string[];
  hideBar: boolean;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
}) {
  // Close the sheet whenever the route changes.
  const firstLoc = useRef(ctx.location);
  useEffect(() => {
    if (ctx.location !== firstLoc.current) setMenuOpen(false);
    firstLoc.current = ctx.location;
  }, [ctx.location, setMenuOpen]);

  const tabs: Array<{ key: string; label: string; href: string; icon: ReactNode; active: boolean; cap?: boolean }> = [
    { key: 'home', label: 'Home', href: '/dashboard', icon: ic.home, active: ctx.location === '/dashboard' },
    { key: 'spark', label: 'Spark', href: '/spark', icon: ic.spark, active: ctx.location === '/spark' },
    { key: 'capture', label: 'Capture', href: '/ideas', icon: ic.plus, active: isIdeaCaptureActive(ctx), cap: true },
    { key: 'advisors', label: 'Advisors', href: '/strategy', icon: ic.users, active: isAdvisorsActive(ctx) },
  ];

  return (
    <>
      {!hideBar && (
        <nav className="tabbar" aria-label="Main">
          {tabs.map((t) => {
            const on = !menuOpen && t.active;
            return (
              <Link
                key={t.key}
                href={t.href}
                className={`tabbar__tab${t.cap ? ' tabbar__tab--cap' : ''}`}
                data-guide-tab={t.key}
                aria-current={on ? 'page' : undefined}
                onClick={
                  t.cap && onCapture
                    ? (e) => {
                        e.preventDefault();
                        setMenuOpen(false);
                        onCapture();
                      }
                    : undefined
                }
              >
                {t.cap ? <span className="tabbar__cap">{t.icon}</span> : t.icon}
                <span>{t.label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            className="tabbar__tab"
            aria-current={menuOpen ? 'page' : undefined}
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {ic.grid}
            <span>Menu</span>
          </button>
        </nav>
      )}
      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} ctx={ctx} isAdmin={isAdmin} roles={roles} />
    </>
  );
}

// ---------- "Everything in EntreDash" sheet ----------

function MenuSheet({
  open,
  onClose,
  ctx,
  isAdmin,
  roles,
}: {
  open: boolean;
  onClose: () => void;
  ctx: NavCtx;
  isAdmin: boolean;
  roles: string[];
}) {
  const dragControls = useDragControls();
  const sheetRef = useRef<HTMLDivElement>(null);
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Collections tile (pinned tags), only when the member has pins.
  const collectionTagIds = useTags((s) => s.collectionTagIds);
  const tagsById = useTags((s) => s.tagsById);
  const collections: NavSectionDef | null = collectionTagIds.length
    ? {
        id: 'collections',
        label: 'Collections',
        hint: '',
        items: collectionTagIds
          .map((id) => tagsById.get(id))
          .filter((t): t is NonNullable<typeof t> => !!t)
          .map<NavItem>((t) => ({
            key: t.id,
            label: t.name,
            href: `/ideas?tag=${encodeURIComponent(t.id)}`,
            glyph: <span>#</span>,
            isActive: (c) => c.location === '/ideas' && c.collectionTagParam === t.id,
          })),
      }
    : null;

  const main = NAV_SECTIONS.filter((s) => !s.roadmap);
  const team = teamSection(roles);
  const tiles: NavSectionDef[] = [
    ...(team ? [team] : []),
    NOTES_SECTION,
    ...main.slice(0, 2),
    ...(collections ? [collections] : []),
    ...main.slice(2),
    ...(isAdmin ? [OPERATOR_SECTION] : []),
  ];
  const roadmap = NAV_SECTIONS.find((s) => s.roadmap);

  const hereId = [...tiles, ...(roadmap ? [roadmap] : [])].find((s) => sectionContainsActive(s, ctx))?.id ?? null;
  const [expanded, setExpanded] = useState<string | null>(hereId);
  useEffect(() => {
    if (open) setExpanded(hereId);
  }, [open, hereId]);

  // Lock page scroll, Escape to close, and move focus into the dialog.
  const returnFocus = useRef<HTMLElement | null>(null);
  const prevOverflow = useRef('');
  const leaving = useRef(false);
  const [, navigate] = useLocation();
  // Held in a ref so the effect below runs once per open, not on every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement as HTMLElement | null;
    leaving.current = false;
    prevOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'Tab' && sheetRef.current) {
        // Keep focus inside the sheet.
        const f = sheetRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => sheetRef.current?.querySelector<HTMLElement>('.msheet__x')?.focus(), 50);
    return () => {
      document.body.style.overflow = prevOverflow.current;
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
      // Only hand focus back when the member stayed on the same page. Focusing
      // the tab bar while a new page mounts made installed phone apps skip the
      // repaint, so the tap looked dead until the screen was dragged.
      if (!leaving.current) returnFocus.current?.focus?.({ preventScroll: true });
    };
  }, [open]);

  /**
   * Menu link tap: close the sheet and unlock page scroll FIRST, then change
   * page on the next frame. Navigating in the same instant as the sheet's
   * close left some phones (installed app) showing the old screen until the
   * member dragged it. If an update is waiting, autoUpdate has already turned
   * the tap into a full page load, so only close.
   */
  const go = (e: ReactMouseEvent, href: string) => {
    leaving.current = true;
    document.body.style.overflow = prevOverflow.current;
    if (e.defaultPrevented) {
      onClose();
      return;
    }
    e.preventDefault();
    onClose();
    requestAnimationFrame(() => navigate(href));
  };

  const onDragEnd = (_: unknown, info: PanInfo) => {
    const h = sheetRef.current?.offsetHeight ?? 600;
    if (info.offset.y > h * 0.3 || info.velocity.y > 600) onClose();
  };

  const renderTile = (s: NavSectionDef) => {
    const isOpen = expanded === s.id;
    const here = s.id === hereId;
    return (
      <div key={s.id} className={`mtile${isOpen ? ' is-open' : ''}${here ? ' is-here' : ''}`}>
        {/* No layout animation here: measuring every tile on each tap made
            taps lag on slower phones. The rows fade in instead. */}
        <button
          type="button"
          className="mtile__hd"
          aria-expanded={isOpen}
          onClick={() => setExpanded(isOpen ? null : s.id)}
        >
          <b>{s.label}</b>
          <span>{s.id === 'collections' ? String(s.items.length) : sectionMeta(s)}</span>
        </button>
        {isOpen && (
          <motion.div
            className="mtile__rows"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18, delay: 0.06 }}
          >
            {s.items.map((item) => {
              const active = item.isActive(ctx);
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  className={`mtile__row${active ? ' is-active' : ''}${item.soon ? ' is-soon' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={(e) => go(e, item.href)}
                >
                  <span className="mtile__glyph" aria-hidden="true">
                    {item.glyph}
                  </span>
                  <span className="mtile__label">{item.label}</span>
                  {item.soon && <span className="sidebar__soon">Soon</span>}
                </Link>
              );
            })}
          </motion.div>
        )}
      </div>
    );
  };

  // The root stays mounted and turns non-interactive the instant the sheet
  // closes. The scrim and sheet only animate out inside it, so even if an exit
  // animation stalls (a slow phone, an installed app resuming) nothing left
  // on screen can catch taps meant for the page underneath.
  return createPortal(
    <div className="msheet-root" data-open={open ? 'true' : 'false'}>
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="scrim"
            className="msheet__scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          />
          <motion.div
            key="sheet"
            ref={sheetRef}
            className="msheet"
            role="dialog"
            aria-modal="true"
            aria-label="Everything in EntreDash"
            initial={{ y: '105%' }}
            animate={{ y: 0 }}
            exit={{ y: '105%' }}
            transition={reduce ? { duration: 0 } : { duration: 0.34, ease: [0.32, 0.72, 0, 1] }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.6 }}
            dragSnapToOrigin
            onDragEnd={onDragEnd}
          >
            <div className="msheet__grab-zone" onPointerDown={(e) => dragControls.start(e)}>
              <div className="msheet__grab" aria-hidden="true" />
              <div className="msheet__head">
                <b>Everything in EntreDash</b>
                <button type="button" className="msheet__x" aria-label="Close menu" onClick={onClose}>
                  {ic.close}
                </button>
              </div>
            </div>
            <div className="msheet__body">
              <div className="msheet__grid">
                  {tiles.map(renderTile)}
                  {roadmap && (
                    <>
                      <div className="msheet__cap">On the roadmap</div>
                      {renderTile(roadmap)}
                    </>
                  )}
              </div>
              <div className="msheet__foot">
                <AccountAccessRow onNavigate={onClose} />
                <InstallAppControl className="mtile__row msheet__footrow" onActivate={onClose}>
                  <span className="mtile__glyph" aria-hidden="true">
                    {NAV_GLYPHS.install}
                  </span>
                  <span className="mtile__label">Install app</span>
                </InstallAppControl>
                <Link
                  href="/settings"
                  className={`mtile__row msheet__footrow${ctx.location.startsWith('/settings') ? ' is-active' : ''}`}
                  onClick={(e) => go(e, '/settings')}
                >
                  <span className="mtile__glyph" aria-hidden="true">
                    {NAV_GLYPHS.gear}
                  </span>
                  <span className="mtile__label">Settings</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </div>,
    document.body,
  );
}
